import { useTranslation } from "react-i18next";
import { InputRow, SelectRow, SwitchRow, DateRow } from "../../Rows";
import { FormCard, FormGrid } from "../../FormCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/pro-regular-svg-icons";
import InputField from "../../InputField";
import {
    currencyOptions,
    getTariffTypeOptions,
    getTaxIncludedOptions,
    TAX_INCLUDED_LOCKED,
} from "../tariffOptions";

const BasicsStep = ({
    basics,
    onChange,
    invalidFields = [],
    countryOptions = [],
    countryDisabled = false,
}) => {
    const { t } = useTranslation();
    const altTexts = basics.altTexts || [];
    const tariffTypeOptions = getTariffTypeOptions(t);
    const taxIncludedOptions = getTaxIncludedOptions(t);

    return (
        <div className="flex flex-col gap-6">
            <FormCard title={t("form.basicInformation")} description={t("tariff.basicInformationDescription")}>
                <FormGrid>
                    <InputRow
                        label={t("common.name")}
                        value={basics.tariffName}
                        onChange={(value) => onChange("tariffName", value)}
                        invalid={invalidFields.includes("tariffName")}
                    />
                    <SelectRow
                        label={t("common.currency")}
                        tooltip={t("tariff.tooltip.currency")}
                        value={basics.currency}
                        options={currencyOptions}
                        required
                        invalid={invalidFields.includes("currency")}
                        onChange={(value) => onChange("currency", value)}
                    />
                    <SelectRow
                        label={t("form.tariffType")}
                        tooltip={t("tariff.tooltip.tariffTypeShort")}
                        value={basics.tariffType}
                        options={tariffTypeOptions}
                        required
                        onChange={(value) => onChange("tariffType", value)}
                    />
                    {countryDisabled ? (
                        <SelectRow
                            label={t("common.country")}
                            tooltip={t("tariff.tooltip.countryCode")}
                            value={basics.countryCode}
                            options={countryOptions}
                            required
                            disabled
                            onChange={() => {}}
                        />
                    ) : (
                        <SelectRow
                            label={t("common.country")}
                            tooltip={t("tariff.tooltip.countryCode")}
                            value={basics.countryCode}
                            options={countryOptions}
                            required
                            invalid={invalidFields.includes("country")}
                            onChange={(value) => onChange("countryCode", value)}
                        />
                    )}
                    <SelectRow
                        label={t("form.taxIncluded")}
                        tooltip={t("tariff.tooltip.taxIncludedLocked")}
                        value={TAX_INCLUDED_LOCKED}
                        options={taxIncludedOptions}
                        disabled
                        onChange={() => {}}
                    />
                    <div className="md:col-span-2">
                        <SwitchRow
                            label={t("form.isPublic")}
                            tooltip={t("tariff.tooltip.isPublic")}
                            checked={basics.isPublic}
                            onChange={(value) => onChange("isPublic", value)}
                        />
                    </div>
                    <DateRow
                        label={t("form.startDateTime")}
                        tooltip={t("tariff.tooltip.startDateTime")}
                        value={basics.startDateTimeUtc}
                        onChange={(value) => onChange("startDateTimeUtc", value)}
                    />
                    <DateRow
                        label={t("form.endDateTime")}
                        tooltip={t("tariff.tooltip.endDateTime")}
                        value={basics.endDateTimeUtc}
                        onChange={(value) => onChange("endDateTimeUtc", value)}
                    />
                    <InputRow
                        label={t("form.preauthorizeAmount")}
                        tooltip={t("tariff.tooltip.preauthorizeAmount")}
                        value={basics.preauthorizeAmount ?? ""}
                        isNumber
                        invalid={invalidFields.includes("preauthorizeAmount")}
                        onChange={(value) => onChange("preauthorizeAmount", value === "" ? null : value)}
                    />
                </FormGrid>
            </FormCard>

            <FormCard title={t("form.altTexts")} description={t("tariff.altTextsDescription")}>
                    <button
                        type="button"
                        className="btn btn-primary mb-4"
                        disabled={altTexts.length >= 4}
                        onClick={() =>
                            onChange("altTexts", [...altTexts, { text: "", language: "" }])
                        }
                    >
                        {t("tariff.addAltText")}
                    </button>
                    {altTexts.map((alt, index) => (
                        <div key={`${alt.language}-${index}`} className="flex items-start gap-4 grid-cols-3 grid py-3 border-b border-mute-200">
                            <InputField
                                label={t("form.altText")}
                                value={alt.text}
                                onChange={(event) => {
                                    const next = [...altTexts];
                                    next[index] = { ...next[index], text: event.target.value };
                                    onChange("altTexts", next);
                                }}
                            />
                            <InputField
                                label={t("common.languageCode")}
                                maxLength={2}
                                value={alt.language}
                                onChange={(event) => {
                                    const next = [...altTexts];
                                    next[index] = { ...next[index], language: event.target.value };
                                    onChange("altTexts", next);
                                }}
                            />
                            <button
                                type="button"
                                className="mt-6"
                                onClick={() => {
                                    const next = [...altTexts];
                                    next.splice(index, 1);
                                    onChange("altTexts", next);
                                }}
                            >
                                <FontAwesomeIcon icon={faTrash} className="text-error-400" />
                            </button>
                        </div>
                    ))}
            </FormCard>
        </div>
    );
};

export default BasicsStep;
